import React, { useEffect, useRef, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'

import styles from './style.module.scss'
import rankIcon from '@assets/images/icon/rank-icon-dark.svg'
// import teamPlaceholder from '@assets/images/placeholder/team-placeholder.jpg'
import CustomLink from '@shared/components/customLink'
import ThemeTable from '@shared/components/themeTable'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GET_ROUNDS, GET_STANDING_DATA } from '@graphql/series/standings.query'
import noImage from '@assets/images/placeholder/article-placeholder.jpg'
import { getImgURL } from '@shared/utils'
import { Nav, Spinner } from 'react-bootstrap'
import { CURRENT_SERIES_LIST } from '@graphql/globalwidget/rankings.query'
import { GET_SUB_PAGES_BY_SLUG } from '@graphql/seo/seo.query'

const CustomSelect = dynamic(() => import('@shared/components/customSelect'))
const MyImage = dynamic(() => import('@shared/components/myImage'))
const Skeleton = dynamic(() => import('@shared-components/skeleton'), { ssr: false })

function SeriesPointTable({ id, title, pageUrl }) {
  const { t } = useTranslation()
  const payload = useRef({ iRoundId: null, iSeriesId: id || '63f052b9d5e097df610db62d', nLimit: 5 })
  const labels = [t('common:No'), t('common:Team'), t('common:M'), t('common:W'), t('common:L'), t('common:PT'), t('common:NRR')]
  const LabelRef = useRef({ sLabel: title, sValue: id })
  const widgetTitle = useRef(title)
  const [URL, setUrl] = useState(pageUrl)

  const [fetchRounds, { data: rounds, loading: roundLoading }] = useLazyQuery(GET_ROUNDS)
  const [fetchStandings, { data, loading }] = useLazyQuery(GET_STANDING_DATA, { fetchPolicy: 'network-only' })
  const [getUrl, { data: seriesUrls }] = useLazyQuery(GET_SUB_PAGES_BY_SLUG)
  const { data: series } = useQuery(CURRENT_SERIES_LIST)

  function handleRound(id) {
    payload.current.iRoundId = id
    fetchStandings({ variables: { input: payload.current } })
  }

  useEffect(() => {
    if (rounds?.fetchSeriesRounds?.length) {
      payload.current.iRoundId = rounds?.fetchSeriesRounds[0]?._id
      handleRound(rounds?.fetchSeriesRounds[0]?._id)
    }
  }, [rounds])

  useEffect(() => {
    payload.current = { iRoundId: null, iSeriesId: id, nLimit: 5 }
    LabelRef.current = { sLabel: title, sValue: id }
    widgetTitle.current = title
    fetchRounds({ variables: { input: { iSeriesId: id || '63f052b9d5e097df610db62d' } } })
    setUrl(pageUrl)
  }, [id])

  useEffect(() => {
    if (seriesUrls?.getSeosBySlug?.length) {
      setUrl(seriesUrls?.getSeosBySlug[0]?.sSlug)
    }
  }, [seriesUrls])

  const seriesList = series?.getCurrentPopularSeries?.aResults?.filter((item) => item?.oSeries?.nTotalTeams > 2)

  const handleSeries = (e) => {
    LabelRef.current.sValue = e?.sValue
    LabelRef.current.sLabel = e?.sLabel
    payload.current.iSeriesId = e?.sValue
    const selected = seriesList?.find((item) => item?.iSeriesId === e?.sValue)

    if (selected?.oSeries?.oSeo?.sSlug || selected?.oSeries?.oCategory?.oSeo?.sSlug) {
      widgetTitle.current = selected?.oSeries?.sSrtTitle || selected?.oSeries?.sTitle
      getUrl({
        variables: { input: { sSlug: selected?.oSeries?.oCategory?.oSeo?.sSlug || selected?.oSeries?.oSeo?.sSlug, eSubType: 's' } }
      })
    }
    fetchRounds({ variables: { input: { iSeriesId: e?.sValue } } })
  }

  const Header = () => (
    <>
      <div className="widget-title d-flex align-items-center justify-content-between">
        <h3 className="small-head d-flex align-items-center text-uppercase mb-0">
          <span className={`${styles.icon} icon me-1`}>
            <MyImage src={rankIcon} alt="winner" width="24" height="24" />
          </span>
          {widgetTitle.current || t('common:IPLPointsTables')}
        </h3>
        <div>
          <CustomLink
            className={`${styles.btn} theme-btn outline-btn xsmall-btn font-semi`}
            href={URL || '/ipl-points-table/?ref=hw'}
            prefetch={false}
          >
            {t('common:ViewAll')}
          </CustomLink>
        </div>
      </div>
    </>
  )

  if (rounds?.fetchSeriesRounds?.length) {
    return (
      <>
        <Header />
        {seriesList && (
          <div className={styles.seriesSelect}>
            <CustomSelect
              options={seriesList.map((series) => ({ sValue: series?.iSeriesId, sLabel: series?.oSeries?.sTitle }))}
              placeholder="Select Series"
              labelKey="sLabel"
              valueKey="sValue"
              value={LabelRef.current}
              defaultSelected
              onChange={(e) => handleSeries(e)}
              inputClass="border-0 mb-01 py-2"
            />
          </div>
        )}
        <section
          className={`${styles.rankingWidget} ${
            payload.current?.iSeriesId === '649ac0872979adc89b9606f0' ? styles.iccRank : ''
          } common-box widget px-2 pb-3 pt-1 position-relative overflow-hidden`}
        >
          {rounds?.fetchSeriesRounds?.length > 1 && (
            <Nav className="text-uppercase equal-width-nav mx-n1 mb-2 mt-1 flex-nowrap" variant="pills">
              {rounds?.fetchSeriesRounds?.map((r) => (
                <Nav.Item key={r?._id} className={`${styles.navItem}`}>
                  <Nav.Link
                    className={`nav-link mx-1 text-nowrap overflow-hidden t-ellipsis ${
                      payload.current.iRoundId === r?._id ? 'active' : ''
                    }`}
                    onClick={() => handleRound(r?._id)}
                  >
                    {r?.sName}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          )}
          <div className={`${styles.rankingTable}`}>
            {(loading || roundLoading) && <Spinner className="position-absolute top-50 start-50" animation="border" />}
            <ThemeTable labels={labels}>
              <>
                {!loading &&
                  data?.fetchSeriesStandings?.map((t, i) => {
                    return (
                      <tr key={t?._id}>
                        <td>{i + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            {t?.oTeam?.eTagStatus === 'a' ? (
                              <CustomLink href={t?.oTeam?.oSeo?.sSlug} className="d-flex align-items-center">
                                <>
                                  <span className={styles.img}>
                                    <MyImage
                                      isLoadOnInteraction
                                      alt={t?.oTeam?.sAbbr || 'team'}
                                      className="rounded-circle"
                                      src={getImgURL(t?.oTeam?.oImg?.sUrl) || noImage}
                                      width="20"
                                      height="20"
                                    />
                                  </span>
                                  <span className="ms-1 theme-text">{t?.oTeam?.sAbbr}</span>
                                </>
                              </CustomLink>
                            ) : (
                              <>
                                <span className={styles.img}>
                                  <MyImage
                                    isLoadOnInteraction
                                    alt={t?.oTeam?.sAbbr || 'team'}
                                    src={getImgURL(t?.oTeam?.oImg?.sUrl) || noImage}
                                    width="20"
                                    height="20"
                                  />
                                </span>
                                <span className="ms-1">{t?.oTeam?.sAbbr}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td>{t?.nPlayed}</td>
                        <td>{t?.nWin}</td>
                        <td>{t?.nLoss}</td>
                        <td>{t?.nPoints}</td>
                        <td>{t?.nNetrr}</td>
                      </tr>
                    )
                  })}
                {loading &&
                  [0, 1, 2, 3, 4].map((_, i) => (
                    <tr key={`${i}_loader`}>
                      <td>
                        <Skeleton height={'20px'} />
                      </td>
                      <td>
                        <Skeleton height={'20px'} />
                      </td>
                      <td>
                        <Skeleton height={'20px'} />
                      </td>
                      <td>
                        <Skeleton height={'20px'} />
                      </td>
                      <td>
                        <Skeleton height={'20px'} />
                      </td>
                      <td>
                        <Skeleton height={'20px'} />
                      </td>
                      <td>
                        <Skeleton height={'20px'} />
                      </td>
                    </tr>
                  ))}
              </>
            </ThemeTable>
          </div>
        </section>
      </>
    )
  } else return null
}

SeriesPointTable.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  pageUrl: PropTypes.string
}
export default SeriesPointTable
